import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Copy, ExternalLink, Slack, Terminal, Cloud, Database, Key, Bot, Zap } from 'lucide-react';

interface ChecklistItem {
  id: string;
  category: string;
  title: string;
  description: string;
  action?: React.ReactNode;
  code?: string;
  link?: { url: string; text: string };
  substeps?: Array<{
    text: string;
    code?: string;
  }>;
}

export default function SlackTestPretty() {
  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Load progress from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('slack-setup-progress');
    if (saved) {
      setCompletedItems(new Set(JSON.parse(saved)));
    }
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem('slack-setup-progress', JSON.stringify(Array.from(completedItems)));
  }, [completedItems]);

  const toggleComplete = (id: string) => {
    const newCompleted = new Set(completedItems);
    if (newCompleted.has(id)) {
      newCompleted.delete(id);
    } else {
      newCompleted.add(id);
    }
    setCompletedItems(newCompleted);
  };

  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const resetProgress = () => {
    setCompletedItems(new Set());
    localStorage.removeItem('slack-setup-progress');
  };

  const checklistItems: ChecklistItem[] = [
    {
      id: 'create-app',
      category: 'Slack App Setup',
      title: 'Create a new Slack App',
      description: 'Create your Claude Code Bridge app in Slack',
      link: {
        url: 'https://api.slack.com/apps',
        text: 'Open Slack Apps Dashboard'
      },
      substeps: [
        { text: 'Click "Create New App" → "From scratch"' },
        { text: 'Name it "Claude Code Bridge"' },
        { text: 'Select your workspace' },
        { text: 'Click "Create App"' }
      ]
    },
    {
      id: 'slash-commands',
      category: 'Slack App Setup',
      title: 'Configure Slash Commands',
      description: 'Add slash commands that will trigger Claude',
      substeps: [
        { text: 'Go to "Slash Commands" in the sidebar' },
        { text: 'Add /claude-deploy', code: 'https://futr-bet-api.brianball533.workers.dev/slack/events' },
        { text: 'Add /claude-test', code: 'https://futr-bet-api.brianball533.workers.dev/slack/events' },
        { text: 'Add /claude-feature', code: 'https://futr-bet-api.brianball533.workers.dev/slack/events' },
        { text: 'Add /claude-debug', code: 'https://futr-bet-api.brianball533.workers.dev/slack/events' }
      ]
    },
    {
      id: 'interactivity',
      category: 'Slack App Setup',
      title: 'Enable Interactivity',
      description: 'Allow Claude to receive button clicks from humans',
      substeps: [
        { text: 'Go to "Interactivity & Shortcuts"' },
        { text: 'Toggle ON "Interactivity"' },
        { text: 'Set Request URL:', code: 'https://futr-bet-api.brianball533.workers.dev/slack/interactive' },
        { text: 'Click "Save Changes"' }
      ]
    },
    {
      id: 'permissions',
      category: 'Slack App Setup',
      title: 'Configure OAuth Permissions',
      description: 'Grant necessary permissions for the bot',
      substeps: [
        { text: 'Go to "OAuth & Permissions"' },
        { text: 'Add Bot Token Scopes:' },
        { text: '• chat:write (send messages)' },
        { text: '• commands (receive slash commands)' },
        { text: '• interactive (handle button clicks)' },
        { text: 'Click "Install to Workspace"' },
        { text: 'Copy the Bot User OAuth Token (xoxb-...)' }
      ]
    },
    {
      id: 'get-secrets',
      category: 'Slack App Setup',
      title: 'Collect Required Secrets',
      description: 'Get your signing secret and bot token',
      substeps: [
        { text: 'From "Basic Information": Copy Signing Secret' },
        { text: 'From "OAuth & Permissions": Copy Bot Token' },
        { text: 'Save both securely - you\'ll need them for Cloudflare' }
      ]
    },
    {
      id: 'install-wrangler',
      category: 'Cloudflare Setup',
      title: 'Install Wrangler CLI',
      description: 'Install Cloudflare\'s CLI tool',
      code: 'npm install -g wrangler'
    },
    {
      id: 'wrangler-login',
      category: 'Cloudflare Setup',
      title: 'Login to Cloudflare',
      description: 'Authenticate with your Cloudflare account',
      code: 'wrangler login'
    },
    {
      id: 'create-database',
      category: 'Cloudflare Setup',
      title: 'Create D1 Database',
      description: 'Create the database for storing tasks',
      code: 'wrangler d1 create slack-claude-db'
    },
    {
      id: 'create-wrangler-toml',
      category: 'Cloudflare Setup',
      title: 'Create wrangler.toml Configuration',
      description: 'Configure your worker deployment',
      code: `cat > wrangler.toml << 'EOF'
name = "futr-bet-api"
main = "workers/slack-claude-bridge.js"
compatibility_date = "2024-01-01"

[[d1_databases]]
binding = "DB"
database_name = "slack-claude-db"
database_id = "YOUR_DATABASE_ID_HERE"

[vars]
SLACK_SIGNING_SECRET = "YOUR_SIGNING_SECRET_HERE"
SLACK_BOT_TOKEN = "xoxb-YOUR-BOT-TOKEN-HERE"
EOF`
    },
    {
      id: 'create-schema',
      category: 'Cloudflare Setup',
      title: 'Create Database Schema',
      description: 'Set up the database tables',
      code: `cat > schema.sql << 'EOF'
CREATE TABLE claude_tasks (
  id TEXT PRIMARY KEY,
  slack_user_id TEXT,
  slack_channel_id TEXT,
  command TEXT,
  text TEXT,
  response_url TEXT,
  status TEXT,
  human_response TEXT,
  last_message TEXT,
  result TEXT,
  summary TEXT,
  thread_ts TEXT,
  created_at DATETIME,
  processed_at DATETIME,
  updated_at DATETIME,
  completed_at DATETIME
);

CREATE TABLE human_responses (
  id TEXT PRIMARY KEY,
  task_id TEXT,
  user_id TEXT,
  user_name TEXT,
  response TEXT,
  created_at DATETIME,
  FOREIGN KEY (task_id) REFERENCES claude_tasks(id)
);

CREATE INDEX idx_tasks_status ON claude_tasks(status);
CREATE INDEX idx_tasks_created ON claude_tasks(created_at);
EOF`
    },
    {
      id: 'init-database',
      category: 'Cloudflare Setup',
      title: 'Initialize Database',
      description: 'Create the tables in your D1 database',
      code: 'wrangler d1 execute slack-claude-db --local --file=schema.sql'
    },
    {
      id: 'deploy-worker',
      category: 'Cloudflare Setup',
      title: 'Deploy Worker',
      description: 'Deploy your worker to Cloudflare',
      code: 'wrangler deploy'
    },
    {
      id: 'update-urls',
      category: 'Cloudflare Setup',
      title: 'Update Slack URLs',
      description: 'Go back to Slack and update all URLs with your deployed worker URL',
      substeps: [
        { text: 'Your worker URL will be:', code: 'https://futr-bet-api.brianball533.workers.dev' },
        { text: 'Update all slash command URLs in Slack to use /slack/events endpoint' },
        { text: 'Update the interactivity URL to use /slack/interactive endpoint' }
      ]
    },
    {
      id: 'install-deps',
      category: 'Claude Code Setup',
      title: 'Install Node Dependencies',
      description: 'Install required packages for the Claude script',
      code: 'npm install node-fetch'
    },
    {
      id: 'configure-script',
      category: 'Claude Code Setup',
      title: 'Configure Claude Script',
      description: 'Update the worker URL in the script',
      code: `# Edit scripts/claude-slack-loop.js
# Update line 5:
const WORKER_URL = 'https://futr-bet-api.brianball533.workers.dev';`
    },
    {
      id: 'run-bridge',
      category: 'Claude Code Setup',
      title: 'Start Claude-Slack Bridge',
      description: 'Run the bridge to start processing Slack commands',
      code: 'node scripts/claude-slack-loop.js'
    },
    {
      id: 'test-command',
      category: 'Testing',
      title: 'Test a Slack Command',
      description: 'Go to Slack and test your setup',
      substeps: [
        { text: 'Go to any channel in your Slack workspace' },
        { text: 'Type: /claude-test hello world' },
        { text: 'You should see Claude respond' },
        { text: 'Try clicking any interactive buttons' }
      ]
    },
    {
      id: 'verify-logs',
      category: 'Testing',
      title: 'Verify Worker Logs',
      description: 'Check Cloudflare dashboard for activity',
      link: {
        url: 'https://dash.cloudflare.com',
        text: 'Open Cloudflare Dashboard'
      },
      substeps: [
        { text: 'Go to Workers & Pages' },
        { text: 'Click on "slack-claude-bridge"' },
        { text: 'Check "Logs" tab for activity' },
        { text: 'Verify requests are being received' }
      ]
    }
  ];

  const categories = [...new Set(checklistItems.map(item => item.category))];
  const categoryProgress = categories.map(cat => {
    const items = checklistItems.filter(item => item.category === cat);
    const completed = items.filter(item => completedItems.has(item.id)).length;
    return { category: cat, completed, total: items.length };
  });

  const totalProgress = (completedItems.size / checklistItems.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 text-white">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center items-center gap-3 mb-4">
            <Slack className="w-10 h-10 text-[#4A154B]" />
            <Zap className="w-6 h-6 text-yellow-400" />
            <Bot className="w-10 h-10 text-blue-400" />
          </div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Claude Code + Slack Integration Setup
          </h1>
          <p className="text-gray-400 text-lg mb-4">
            Create a human-in-the-loop development workflow with Claude Code and Slack
          </p>
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg px-4 py-2 inline-flex items-center gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-green-300">Compliant with Slack's AI App Guidelines</span>
          </div>

          {/* Progress Bar */}
          <div className="max-w-2xl mx-auto mb-4">
            <div className="bg-gray-800 rounded-full h-4 overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-full transition-all duration-500 ease-out"
                style={{ width: `${totalProgress}%` }}
              />
            </div>
            <p className="text-sm text-gray-400 mt-2">
              {completedItems.size} of {checklistItems.length} steps completed ({Math.round(totalProgress)}%)
            </p>
          </div>

          {/* Category Progress */}
          <div className="flex justify-center gap-6 flex-wrap mb-6">
            {categoryProgress.map(({ category, completed, total }) => (
              <div key={category} className="text-center">
                <div className="text-xs text-gray-500 mb-1">{category}</div>
                <div className="text-sm font-mono">
                  {completed}/{total}
                </div>
              </div>
            ))}
          </div>

          {/* Reset Button */}
          {completedItems.size > 0 && (
            <button
              onClick={resetProgress}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Reset Progress
            </button>
          )}
        </div>

        {/* Checklist */}
        <div className="space-y-8">
          {categories.map(category => (
            <div key={category} className="bg-gray-800/50 rounded-xl p-6 backdrop-blur-sm border border-gray-700">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                {category === 'Slack App Setup' && <Slack className="w-6 h-6" />}
                {category === 'Cloudflare Setup' && <Cloud className="w-6 h-6" />}
                {category === 'Claude Code Setup' && <Terminal className="w-6 h-6" />}
                {category === 'Testing' && <Zap className="w-6 h-6" />}
                {category}
              </h2>

              <div className="space-y-4">
                {checklistItems.filter(item => item.category === category).map(item => (
                  <div
                    key={item.id}
                    className={`border rounded-lg p-4 transition-all ${
                      completedItems.has(item.id)
                        ? 'border-green-500/50 bg-green-500/5'
                        : 'border-gray-600 bg-gray-900/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => toggleComplete(item.id)}
                        className="mt-1 flex-shrink-0"
                      >
                        {completedItems.has(item.id) ? (
                          <CheckCircle className="w-6 h-6 text-green-400" />
                        ) : (
                          <Circle className="w-6 h-6 text-gray-500 hover:text-white" />
                        )}
                      </button>

                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
                        <p className="text-gray-400 text-sm mb-3">{item.description}</p>

                        {/* Code Block */}
                        {item.code && (
                          <div className="relative group mb-3">
                            <pre className="bg-black/50 rounded p-3 text-sm overflow-x-auto border border-gray-700">
                              <code className="text-green-400">{item.code}</code>
                            </pre>
                            <button
                              onClick={() => copyToClipboard(item.code, item.id)}
                              className="absolute top-2 right-2 p-2 bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              {copiedId === item.id ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-400" />
                              )}
                            </button>
                          </div>
                        )}

                        {/* External Link */}
                        {item.link && (
                          <a
                            href={item.link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm mb-3"
                          >
                            {item.link.text}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}

                        {/* Substeps */}
                        {item.substeps && (
                          <ul className="space-y-2 ml-4">
                            {item.substeps.map((step, idx) => (
                              <li key={idx} className="text-sm text-gray-300">
                                <span className="text-gray-500 mr-2">•</span>
                                {step.text}
                                {step.code && (
                                  <div className="inline-flex items-center gap-2 ml-2">
                                    <code className="px-2 py-1 bg-black/30 rounded text-xs text-green-400">
                                      {step.code}
                                    </code>
                                    <button
                                      onClick={() => copyToClipboard(step.code, `${item.id}-step-${idx}`)}
                                      className="p-1 bg-gray-800 rounded hover:bg-gray-700 transition-colors"
                                      title="Copy URL"
                                    >
                                      {copiedId === `${item.id}-step-${idx}` ? (
                                        <CheckCircle className="w-3 h-3 text-green-400" />
                                      ) : (
                                        <Copy className="w-3 h-3 text-gray-400" />
                                      )}
                                    </button>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Completion Message */}
        {totalProgress === 100 && (
          <div className="mt-12 text-center p-8 bg-gradient-to-r from-green-900/20 to-blue-900/20 rounded-xl border border-green-500/30">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Setup Complete! 🎉</h2>
            <p className="text-gray-300 mb-4">
              Your Claude Code + Slack integration is ready to use.
            </p>
            <div className="bg-black/30 rounded-lg p-4 text-left max-w-md mx-auto">
              <p className="text-sm font-mono text-green-400 mb-2">Try it out:</p>
              <code className="text-sm text-gray-300">
                /claude-feature Add dark mode to my app
              </code>
            </div>
          </div>
        )}

        {/* Quick Reference */}
        <div className="mt-12 p-6 bg-gray-800/30 rounded-xl border border-gray-700">
          <h3 className="text-xl font-bold mb-4">Quick Reference</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold text-purple-400 mb-2">Available Commands</h4>
              <ul className="space-y-1 text-gray-300">
                <li><code className="text-green-400">/claude-deploy</code> - Deploy your application</li>
                <li><code className="text-green-400">/claude-test</code> - Run tests</li>
                <li><code className="text-green-400">/claude-feature</code> - Develop new features</li>
                <li><code className="text-green-400">/claude-debug</code> - Debug issues</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-purple-400 mb-2">File Locations</h4>
              <ul className="space-y-1 text-gray-300">
                <li><code>workers/slack-claude-bridge.js</code> - Worker code</li>
                <li><code>scripts/claude-slack-loop.js</code> - Claude script</li>
                <li><code>wrangler.toml</code> - Cloudflare config</li>
                <li><code>schema.sql</code> - Database schema</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}